<template>
  <div class="cn-footer">
    <div class="container">
      <div class="cn-footer__top">
        <div class="cn-footer__top-left">
          <div class="cn-footer__top-content">
            <div>
              <AppLogo />
            </div>
            <div class="cn-footer__mobile-menu">
              <AppFooterMenuMobile :menu="menu" />
            </div>
            <div class="cn-footer__socials">
              <UiLink
                v-for="social in socials"
                :key="social.name"
                :to="social.src"
                class="cn-footer__socials-item"
                rel="nofollow"
                target="_blank"
                :aria-label="social.name"
              >
                <UiIcon :name="social.name" />
              </UiLink>
            </div>
            <div class="cn-footer__controls">
              <UiButton
                variant="yellow"
                block
                @click="supportModal !== null ? supportModal.open() : null"
              >
                {{ $t('write_to_us') }}
              </UiButton>
              <UiModal ref="supportModal" :title="$t('submit_an_inquiry')">
                <SupportForm />
              </UiModal>
            </div>
          </div>
        </div>
        <div class="cn-footer__top-right">
          <AppFooterMenu :menu="menu" />
        </div>
      </div>
      <div class="cn-footer__bottom">
        <div class="cn-footer__copyright">
          {{ $t('all_rights_reserved') }} © 2014-{{ new Date().getFullYear() }}
          CANYON
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useSocials } from '@/composables';
import SupportForm from '@/components/SupportForm';
import AppLogo from '@/components/AppLogo';
import AppFooterMenuMobile from '@/components/AppFooterMenuMobile';
import AppFooterMenu from '@/components/AppFooterMenu';
import UiModal from '@/components/ui/UiModal';
import UiLink from '@/components/ui/UiLink';
import UiIcon from '@/components/ui/UiIcon';
import UiButton from '@/components/ui/UiButton';

interface Props {
  menu: any[];
}

defineProps<Props>();

const { socials } = useSocials();
const supportModal = ref<null | typeof UiModal>(null);
</script>

<style lang="scss">
.cn-footer {
  --ab-canyon-footer-color-bg: var(--ab-canyon-color-neutral-light);
  --ab-canyon-footer-socials-item-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-footer-socials-item-icon-color-bg: var(--ab-canyon-color-primary);
  --ab-canyon-footer-copyright-color-text: var(--ab-canyon-color-neutral-dark);

  background-color: var(--ab-canyon-footer-color-bg);
  padding-top: var(--size-4xl);
  padding-bottom: var(--size-4xl);
  &__top {
    gap: calc(4 * var(--size-base));
    justify-content: space-between;
    flex-wrap: wrap;
    display: flex;
    @media (min-width: 992px) {
      flex-wrap: nowrap;
    }
  }
  &__top-left {
    width: 100%;
    @media (min-width: 992px) {
      width: auto;
    }
  }
  &__top-right {
    display: none;
    @media (min-width: 992px) {
      display: block;
    }
  }
  &__top-content {
    @media (min-width: 992px) {
      width: 192px;
    }
  }
  &__mobile-menu {
    margin-top: var(--size-3xl);

    @media (min-width: 992px) {
      display: none;
    }
  }
  &__socials {
    gap: var(--size-xl);
    flex-wrap: wrap;
    display: flex;
    margin-top: var(--size-3xl);
  }

  &__socials-item {
    background-color: var(--ab-canyon-footer-socials-item-color-bg);
    transition-duration: 0.5s;
    transition-property: box-shadow, transform;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    justify-content: center;
    align-items: center;
    width: 3rem;
    height: 3rem;
    display: flex;
    &:hover {
      box-shadow:
        #00000000 0px 0px 0px 0px,
        #00000000 0px 0px 0px 0px,
        #0000000d 0px 1px 2px 0px;
      transform: translateY(-1px);
    }
    svg {
      color: var(--ab-canyon-footer-socials-item-icon-color-bg);
      width: 1.75rem;
      height: 1.75rem;
    }
  }
  &__controls {
    margin-top: var(--size-3xl);
  }
  &__bottom {
    margin-top: var(--size-3xl);
  }
  &__copyright {
    color: var(--ab-canyon-footer-copyright-color-text);
  }
}
</style>
